import React from 'react';
import PropTypes from 'prop-types';
import LayoutPlaceholderCard from '../../../../components/shared/LayoutPlaceholderCard';
import { getCardPlaceholderMeta } from '../../../../utils/getCardDisplayTitle';
import { getHospitalLayoutSkeletonVariant } from '../../../../utils/hospitalLayoutPlaceholderSkeleton';
import { getLayoutPreviewSharePanelProps } from '../../../../utils/layoutPreviewShareBadge';
import {
	arePreviewPropsEqual,
	DaysPreviewBody,
	DcerPreviewBody,
	DxPreviewBody,
	FloorsPreviewBody,
	HospitalizationsPreviewBody,
	InsurancePreviewBody,
	NinetyDaysPreviewBody,
	resolveDynamicPreviewBody,
	ReturnsPreviewBody,
	ShiftPreviewBody,
	SliderPreviewBody,
	UserListPreviewBody,
} from '../../../../components/shared/layoutPreview';

/**
 * Layout-mode preview renderer for the Hospital page.
 *
 * See `components/shared/layoutPreview/` for the shared primitives (shell,
 * memoized bodies, dynamic-card resolver, custom `React.memo` comparator).
 * This file just maps each Hospital-specific `card.component` value to the
 * matching shared body, or delegates to the dynamic resolver.
 *
 * If a future Hospital card has a unique silhouette, add a page-specific
 * `React.memo(() => <PreviewShell>…</PreviewShell>)` next to this file and
 * slot it into `PREVIEW_BY_COMPONENT` — don't pollute the shared module
 * with page-specific visuals.
 */

// Map: `card.component` → memoized preview body component.
// Keys match the `case` values in `Hospital.js#renderCard`'s switch.
const PREVIEW_BY_COMPONENT = {
	hospitalizations: HospitalizationsPreviewBody,
	dcer: DcerPreviewBody,
	insurance: InsurancePreviewBody,
	returns: ReturnsPreviewBody,
	ninetyDays: NinetyDaysPreviewBody,
	floors: FloorsPreviewBody,
	doctor: UserListPreviewBody,
	nurse: UserListPreviewBody,
	days: DaysPreviewBody,
	dx: DxPreviewBody,
	shift: ShiftPreviewBody,
	hospital: SliderPreviewBody,
	// `dynamic` is NOT in this map — it's resolved per card below.
};

const HospitalCardLayoutPreview = ({ card, dynamicCards, sharedUsersByTabId }) => {
	const { title: placeholderTitle } = getCardPlaceholderMeta(card, {
		dynamicCards,
	});

	let PreviewBody = null;
	let previewBodyProps = {};
	if (card?.component === 'dynamic') {
		PreviewBody = resolveDynamicPreviewBody(card?.data);
		// Dynamic/custom/combine cards should use generic test labels where applicable.
		previewBodyProps = { role: 'generic', useGenericLabels: true };
	} else if (card?.component) {
		PreviewBody = PREVIEW_BY_COMPONENT[card.component] || null;
		if (card.component === 'doctor') {
			previewBodyProps = { role: 'doctor' };
		} else if (card.component === 'nurse') {
			previewBodyProps = { role: 'nurse' };
		}
	}

	return (
		<LayoutPlaceholderCard
			variant="card"
			title={placeholderTitle}
			width={card?.width}
			sharePanel={getLayoutPreviewSharePanelProps(card, { sharedUsersByTabId })}
			skeletonVariant={getHospitalLayoutSkeletonVariant(card)}
			body={PreviewBody ? <PreviewBody {...previewBodyProps} /> : null}
		/>
	);
};

HospitalCardLayoutPreview.propTypes = {
	card: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		component: PropTypes.string,
		type: PropTypes.string,
		width: PropTypes.number,
		data: PropTypes.object,
	}).isRequired,
	dynamicCards: PropTypes.array,
	sharedUsersByTabId: PropTypes.object,
};

HospitalCardLayoutPreview.defaultProps = {
	dynamicCards: [],
	sharedUsersByTabId: undefined,
};

export default React.memo(HospitalCardLayoutPreview, arePreviewPropsEqual);
